<template>
  <div style="display: contents">
    <template v-for="node in nodes">
      <tr
        :key="`takeoff-boq-row-${node.id}`"
        class="weekly-takeoff-boq-tree-rows__row"
        :class="{
          'weekly-takeoff-boq-tree-rows__row--nested': depth > 0,
          'weekly-takeoff-boq-tree-rows__row--expandable': canExpand(node),
        }"
        @click="canExpand(node) && $emit('toggle', node.id)"
      >
        <td class="weekly-takeoff-boq-tree-rows__td weekly-takeoff-boq-tree-rows__td--name">
          <div class="weekly-takeoff-boq-tree-rows__name-cell" :style="indentStyle">
            <button
              v-if="canExpand(node)"
              type="button"
              class="weekly-takeoff-boq-tree-rows__expand-btn"
              :aria-expanded="isExpanded(node.id)"
              @click.stop="$emit('toggle', node.id)"
            >
              <b-icon
                pack="fas"
                :icon="isExpanded(node.id) ? 'chevron-down' : 'chevron-right'"
                size="is-small"
              />
            </button>
            <span v-else class="weekly-takeoff-boq-tree-rows__expand-spacer" />
            <span class="weekly-takeoff-boq-tree-rows__name">{{ node.name }}</span>
          </div>
        </td>
        <td
          v-for="statKey in statsKeys"
          :key="`takeoff-boq-stat-${node.id}-${statKey}`"
          class="weekly-takeoff-boq-tree-rows__td weekly-takeoff-boq-tree-rows__td--stat"
          :class="statCellClass(statKey, node)"
        >
          <div
            v-if="isStageHhPctMonthInput(node, statKey)"
            class="weekly-takeoff-boq-tree-rows__pct-field"
            @click.stop
          >
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              class="weekly-takeoff-boq-tree-rows__pct-input"
              :value="node.takeoffStats[statKey]"
              @input="onHhPctMonthInput(node, statKey, $event)"
            >
            <span class="weekly-takeoff-boq-tree-rows__pct-suffix">%</span>
            <span
              v-if="isHhPctKeyDirty(node, statKey)"
              class="weekly-takeoff-boq-tree-rows__hh-pct-actions"
            >
              <button
                type="button"
                class="weekly-takeoff-boq-tree-rows__hh-pct-btn weekly-takeoff-boq-tree-rows__hh-pct-btn--save"
                :disabled="node.savingHhPct"
                :title="$t('save')"
                @click="saveHhPctMonths(node)"
              >
                <b-icon pack="fas" icon="check" size="is-small" />
              </button>
              <button
                type="button"
                class="weekly-takeoff-boq-tree-rows__hh-pct-btn weekly-takeoff-boq-tree-rows__hh-pct-btn--cancel"
                :disabled="node.savingHhPct"
                :title="$t('cancel')"
                @click="cancelHhPctMonth(node, statKey)"
              >
                <b-icon pack="fas" icon="times" size="is-small" />
              </button>
            </span>
          </div>
          <span
            v-else-if="statKey === 'sigma_percent' && isStage(node)"
            class="weekly-takeoff-boq-tree-rows__sigma"
            :class="sigmaPercentClass(node)"
          >
            {{ formatSigmaPercent(node) }}
          </span>
          <template v-else>
            {{ formatStat(node.takeoffStats, statKey) }}
          </template>
        </td>
      </tr>

      <tr
        v-if="canExpand(node) && isExpanded(node.id) && isLoading(node.id)"
        :key="`takeoff-boq-loading-${node.id}`"
      >
        <td
          :colspan="columnCount"
          class="px-4 py-3 text-center text-gray-500 border border-gray-200"
        >
          {{ $t('loading') }}...
        </td>
      </tr>

      <weekly-takeoff-boq-tree-rows
        v-else-if="canExpand(node) && isExpanded(node.id)"
        :key="`takeoff-boq-children-${node.id}`"
        :project-id="projectId"
        :nodes="childrenByParent[node.id] || []"
        :depth="depth + 1"
        :expanded-ids="expandedIds"
        :column-count="columnCount"
        :stats-keys="statsKeys"
        :map-stats-keys="mapStatsKeys"
        :with-takeoff-stats="withTakeoffStats"
        :hh-per-person-month="hhPerPersonMonth"
        @toggle="$emit('toggle', $event)"
      />
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import {
  applyTakeoffMonthlyDerivedFromApi,
  applyTakeoffMonthlyStatsFromApi,
  takeoffMonthlyDerivedSnapshot,
} from './takeoff-monthly-stats'

const ROW_KIND_DISCIPLINE = 1
const ROW_KIND_STAGE = 2
const QUANTITY_DECIMALS = 5

export default {
  name: 'weekly-takeoff-boq-tree-rows',

  components: {
    WeeklyTakeoffBoqTreeRows: null,
  },

  beforeCreate() {
    this.$options.components.WeeklyTakeoffBoqTreeRows = this.$options
  },

  props: {
    projectId: {
      type: [Number, String],
      required: true,
    },
    nodes: {
      type: Array,
      default: () => [],
    },
    depth: {
      type: Number,
      default: 0,
    },
    expandedIds: {
      type: Array,
      default: () => [],
    },
    columnCount: {
      type: Number,
      default: 9,
    },
    statsKeys: {
      type: Array,
      default: () => ([
        'unit',
        'total_scope',
        'executed_accum',
        'balance',
        'adjusted_man_hour_index',
        'scheduled_man_hours',
        'hh_to_finish',
        'people_needed',
      ]),
    },
    mapStatsKeys: {
      type: Array,
      default: () => [],
    },
    withTakeoffStats: {
      type: Boolean,
      default: false,
    },
    hhPerPersonMonth: {
      type: Number,
      default: null,
    },
  },

  emits: ['toggle'],

  data() {
    return {
      childrenByParent: {},
      loadingParentIds: [],
      loadedParentIds: [],
    }
  },

  computed: {
    indentStyle() {
      if (this.depth <= 0) {
        return null
      }
      return { paddingLeft: `${this.depth * 1.25}rem` }
    },

    mappingStatsKeys() {
      return this.mapStatsKeys.length ? this.mapStatsKeys : this.statsKeys
    },
  },

  watch: {
    expandedIds: {
      deep: true,
      immediate: true,
      handler(ids) {
        this.nodes.forEach((node) => {
          if (
            this.canExpand(node)
            && ids.includes(node.id)
            && !this.loadedParentIds.includes(node.id)
            && !this.loadingParentIds.includes(node.id)
          ) {
            this.loadChildren(node.id)
          }
        })
      },
    },
  },

  methods: {
    canExpand(node) {
      return Number(node.childrenCount || 0) > 0
    },

    isExpanded(nodeId) {
      return this.expandedIds.includes(nodeId)
    },

    isLoading(nodeId) {
      return this.loadingParentIds.includes(nodeId)
    },

    mapNode(raw) {
      const rowKind = raw.row_kind != null ? Number(raw.row_kind) : null
      const takeoffStats = {
        unit: raw.unit ?? null,
        total_scope: raw.total_scope ?? null,
        executed_accum: raw.executed_accum ?? null,
        balance: raw.balance ?? null,
        adjusted_man_hour_index: raw.adjusted_man_hour_index ?? null,
        scheduled_man_hours: raw.scheduled_man_hours ?? null,
        hh_to_finish: raw.hh_to_finish ?? null,
        people_needed: raw.people_needed ?? null,
        ...this.emptyMonthlyStats(),
      }
      applyTakeoffMonthlyStatsFromApi(takeoffStats, raw, {
        mappingStatsKeys: this.mappingStatsKeys,
        hhPctKeys: this.hhPctMonthKeysFromMapping(),
      })

      return {
        id: raw.id,
        code: raw.code || '',
        name: raw.name || '',
        rowKind,
        childrenCount: Number(raw.children_count) || 0,
        takeoffStats,
        savedHhPct: this.snapshotHhPct(takeoffStats),
        savedMonthlyDerived: takeoffMonthlyDerivedSnapshot(raw),
        savingHhPct: false,
        badgeLabel: rowKind === ROW_KIND_STAGE
          ? 'STAGE'
          : (rowKind === ROW_KIND_DISCIPLINE ? 'DISC' : (raw.code || '')),
        _raw: raw,
      }
    },

    applyMonthlyDistributions(stats, distributions, derivedRaw = null) {
      applyTakeoffMonthlyStatsFromApi(stats, derivedRaw || {}, {
        distributions,
        mappingStatsKeys: this.mappingStatsKeys,
        hhPctKeys: this.hhPctMonthKeysFromMapping(),
      })
    },

    hhPctMonthKeysFromMapping() {
      return this.mappingStatsKeys.filter((key) => String(key).startsWith('hh_pct_month_'))
    },

    restoreSavedMonthlyDerived(stats, node) {
      applyTakeoffMonthlyDerivedFromApi(
        stats,
        node.savedMonthlyDerived || {},
        this.mappingStatsKeys,
      )
    },

    snapshotHhPct(stats) {
      const snapshot = {}
      this.hhPctMonthKeys().forEach((key) => {
        snapshot[key] = Number(stats[key]) || 0
      })
      return snapshot
    },

    sigmaPercentFromStats(stats) {
      return this.hhPctMonthKeys().reduce((sum, key) => {
        const value = Number(stats[key])
        return sum + (Number.isFinite(value) ? value : 0)
      }, 0)
    },

    formatStat(stats, key) {
      if (this.isMonthlyPlaceholderKey(key)) {
        if (key === 'sigma_percent' || String(key).startsWith('hh_pct_month_')) {
          return this.formatPercentNumber((stats && stats[key] != null) ? stats[key] : 0)
        }
        return this.formatQuantityNumber(stats && stats[key])
      }
      if (key === 'unit') {
        if (!stats || stats[key] == null || stats[key] === '') {
          return '—'
        }
        return stats[key]
      }
      return this.formatQuantityNumber(stats && stats[key])
    },

    formatQuantityNumber(value) {
      if (value == null || value === '') {
        return '—'
      }
      const number = Number(value)
      if (!Number.isFinite(number)) {
        return '—'
      }
      return number.toLocaleString('es-ES', {
        minimumFractionDigits: 0,
        maximumFractionDigits: QUANTITY_DECIMALS,
      })
    },

    formatPercentNumber(value) {
      const number = Number(value) || 0
      return `${number.toFixed(2).replace('.', ',')}%`
    },

    isStage(node) {
      return Number(node.rowKind) === ROW_KIND_STAGE
    },

    isStageHhPctMonthInput(node, key) {
      return this.isStage(node) && String(key).startsWith('hh_pct_month_')
    },

    hhPctMonthKeys() {
      return this.statsKeys.filter((key) => String(key).startsWith('hh_pct_month_'))
    },

    sigmaPercentValue(node) {
      return this.sigmaPercentFromStats(node.takeoffStats || {})
    },

    formatSigmaPercent(node) {
      return this.formatPercentNumber(this.sigmaPercentValue(node))
    },

    sigmaPercentClass(node) {
      const total = this.sigmaPercentValue(node)
      return {
        'weekly-takeoff-boq-tree-rows__sigma--ok': Math.abs(total - 100) < 0.01,
        'weekly-takeoff-boq-tree-rows__sigma--bad': Math.abs(total - 100) >= 0.01,
      }
    },

    isHhPctDirty(node) {
      if (!this.isStage(node) || !node.savedHhPct) {
        return false
      }
      return this.hhPctMonthKeys().some((key) => this.isHhPctKeyDirty(node, key))
    },

    isHhPctKeyDirty(node, key) {
      if (!this.isStage(node) || !node.savedHhPct) {
        return false
      }
      const current = Number(node.takeoffStats[key]) || 0
      const saved = Number(node.savedHhPct[key]) || 0
      return Math.abs(current - saved) > 0.0001
    },

    onHhPctMonthInput(node, key, event) {
      const parsed = parseFloat(event.target.value)
      let value = Number.isFinite(parsed) ? parsed : 0
      if (value < 0) {
        value = 0
      }
      if (value > 100) {
        value = 100
      }
      value = Math.round(value * 100) / 100
      this.$set(node.takeoffStats, key, value)
      this.$set(node.takeoffStats, 'sigma_percent', this.sigmaPercentValue(node))
    },

    cancelHhPctMonth(node, key) {
      this.$set(node.takeoffStats, key, Number(node.savedHhPct[key]) || 0)
      this.$set(node.takeoffStats, 'sigma_percent', this.sigmaPercentValue(node))
      this.restoreSavedMonthlyDerived(node.takeoffStats, node)
    },

    cancelHhPctMonths(node) {
      this.hhPctMonthKeys().forEach((key) => {
        this.$set(node.takeoffStats, key, Number(node.savedHhPct[key]) || 0)
      })
      this.$set(node.takeoffStats, 'sigma_percent', this.sigmaPercentValue(node))
      this.restoreSavedMonthlyDerived(node.takeoffStats, node)
    },

    async saveHhPctMonths(node) {
      if (!this.projectId || node.savingHhPct || !this.isHhPctDirty(node)) {
        return
      }

      const months = this.hhPctMonthKeys()
        .filter((key) => {
          const current = Number(node.takeoffStats[key]) || 0
          const saved = Number(node.savedHhPct[key]) || 0
          return Math.abs(current - saved) > 0.0001
        })
        .map((key) => ({
          year_month: key.replace('hh_pct_month_', ''),
          percent: Number(node.takeoffStats[key]) || 0,
        }))

      if (!months.length) {
        return
      }

      this.$set(node, 'savingHhPct', true)
      try {
        const { data } = await axios.put(
          `/api/v2/project/${this.projectId}/boq-roots/${node.id}/monthly-distributions`,
          { months }
        )
        if (!(data && data.success)) {
          throw new Error('save_failed')
        }

        const nextStats = { ...node.takeoffStats }
        this.hhPctMonthKeys().forEach((key) => {
          nextStats[key] = 0
        })
        this.applyMonthlyDistributions(nextStats, data.monthly_distributions || [], data)
        this.$set(node, 'takeoffStats', nextStats)
        this.$set(node, 'savedHhPct', this.snapshotHhPct(nextStats))
        this.$set(node, 'savedMonthlyDerived', takeoffMonthlyDerivedSnapshot(data))
        this.$notify.success('save_successful')
      } catch (error) {
        this.$notify.error('error_saving')
      } finally {
        this.$set(node, 'savingHhPct', false)
      }
    },

    emptyMonthlyStats() {
      const stats = {}
      this.mappingStatsKeys.forEach((key) => {
        if (this.isMonthlyPlaceholderKey(key)) {
          stats[key] = 0
        }
      })
      return stats
    },

    isMonthlyPlaceholderKey(key) {
      const value = String(key)
      return value === 'sigma_percent'
        || value.startsWith('hh_pct_month_')
        || value.startsWith('people_month_')
        || value.startsWith('units_month_')
    },

    hasStatValue(stats, key) {
      if (this.isMonthlyPlaceholderKey(key)) {
        return true
      }
      return !!(stats && stats[key] != null && stats[key] !== '')
    },

    statCellClass(statKey, node) {
      const stats = node.takeoffStats
      const hasValue = this.hasStatValue(stats, statKey)
      return {
        'weekly-takeoff-boq-tree-rows__td--empty': !hasValue && !this.isStageHhPctMonthInput(node, statKey),
        'weekly-takeoff-boq-tree-rows__td--executed': hasValue && statKey === 'executed_accum',
        'weekly-takeoff-boq-tree-rows__td--hh-total': hasValue && statKey === 'scheduled_man_hours',
        'weekly-takeoff-boq-tree-rows__td--people': hasValue && statKey === 'people_needed',
        'weekly-takeoff-boq-tree-rows__td--pct-edit': this.isStageHhPctMonthInput(node, statKey),
      }
    },

    async loadChildren(parentId) {
      if (!this.projectId || this.loadingParentIds.includes(parentId)) {
        return
      }

      this.loadingParentIds = [...this.loadingParentIds, parentId]
      try {
        const params = { parent_id: parentId }
        if (this.withTakeoffStats) {
          params.with_takeoff_stats = 1
        }
        const { data } = await axios.get(
          `/api/v2/project/${this.projectId}/boq-roots`,
          { params }
        )
        const list = (data && data.success && data.boq_roots) ? data.boq_roots : []
        this.$set(this.childrenByParent, parentId, list.map((node) => this.mapNode(node)))
        if (!this.loadedParentIds.includes(parentId)) {
          this.loadedParentIds = [...this.loadedParentIds, parentId]
        }
      } catch (error) {
        this.$set(this.childrenByParent, parentId, [])
        this.$notify.error('error_loading_data')
      } finally {
        this.loadingParentIds = this.loadingParentIds.filter((id) => id !== parentId)
      }
    },
  },
}
</script>
<style scoped>
.weekly-takeoff-boq-tree-rows__row {
  background-color: #f9fafb;
  cursor: default;
}

.weekly-takeoff-boq-tree-rows__row--expandable {
  cursor: pointer;
}

.weekly-takeoff-boq-tree-rows__row:hover {
  background-color: #f3f4f6;
}

.weekly-takeoff-boq-tree-rows__row--nested {
  background-color: #ffffff;
}

.weekly-takeoff-boq-tree-rows__row--nested:hover {
  background-color: #f9fafb;
}

.weekly-takeoff-boq-tree-rows__td {
  padding: 0.45rem 0.75rem;
  border: 1px solid #d1d5db;
  vertical-align: middle;
  text-align: center;
  color: #374151;
}

.weekly-takeoff-boq-tree-rows__td--name {
  text-align: left;
  min-width: 16rem;
}

.weekly-takeoff-boq-tree-rows__td--empty {
  color: #9ca3af;
}

.weekly-takeoff-boq-tree-rows__td--executed {
  color: #15803d;
  font-weight: 600;
}

.weekly-takeoff-boq-tree-rows__td--hh-total {
  color: #15803d;
  font-weight: 600;
}

.weekly-takeoff-boq-tree-rows__td--people {
  color: #b45309;
  font-weight: 600;
}

.weekly-takeoff-boq-tree-rows__td--pct-edit {
  padding: 0.25rem 0.4rem;
}

.weekly-takeoff-boq-tree-rows__pct-field {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  justify-content: center;
}

.weekly-takeoff-boq-tree-rows__pct-input {
  width: 4.25rem;
  height: 1.75rem;
  padding: 0.15rem 0.35rem;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  color: #111827;
  font-size: 0.75rem;
  text-align: right;
  background: #fff;
}

.weekly-takeoff-boq-tree-rows__pct-input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 1px #2563eb;
}

.weekly-takeoff-boq-tree-rows__pct-suffix {
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 600;
}

.weekly-takeoff-boq-tree-rows__sigma {
  font-weight: 700;
  white-space: nowrap;
}

.weekly-takeoff-boq-tree-rows__sigma--ok {
  color: #15803d;
}

.weekly-takeoff-boq-tree-rows__sigma--bad {
  color: #b91c1c;
}

.weekly-takeoff-boq-tree-rows__name-cell {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
}

.weekly-takeoff-boq-tree-rows__expand-btn {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  color: #0f766e;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.weekly-takeoff-boq-tree-rows__expand-spacer {
  display: inline-block;
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
}

.weekly-takeoff-boq-tree-rows__name {
  overflow: hidden;
  color: #0f766e;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.weekly-takeoff-boq-tree-rows__hh-pct-actions {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 0.15rem;
  margin-left: 0.15rem;
}

.weekly-takeoff-boq-tree-rows__hh-pct-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 0.25rem;
  cursor: pointer;
  background: transparent;
}

.weekly-takeoff-boq-tree-rows__hh-pct-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.weekly-takeoff-boq-tree-rows__hh-pct-btn--save {
  color: #15803d;
  border-color: #86efac;
  background: #f0fdf4;
}

.weekly-takeoff-boq-tree-rows__hh-pct-btn--cancel {
  color: #b91c1c;
  border-color: #fca5a5;
  background: #fef2f2;
}
</style>
